import React, { useEffect, useRef, useState } from 'react';
import { X, Phone, MessageCircle, Loader2, ArrowUpRight } from 'lucide-react';

/**
 * Mandatory lead-capture gate shown BEFORE a buyer can call / whatsapp a dealer.
 * Editorial / sharp / minimalist styling matching the dealer microsite skin.
 */
const BuyerLeadModal = ({ open, onClose, action = 'whatsapp', context = {}, submitting = false, onSubmit }) => {
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [error, setError] = useState('');
  const nameRef = useRef(null);

  useEffect(() => {
    if (open) {
      setName('');
      setMobile('');
      setError('');
      setTimeout(() => nameRef.current?.focus(), 50);
    }
  }, [open]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanName = name.trim();
    const cleanMobile = mobile.replace(/[^0-9]/g, '');
    if (cleanName.length < 2) {
      setError('Please enter your full name.');
      return;
    }
    if (cleanMobile.length < 10) {
      setError('Please enter a valid 10-digit mobile number.');
      return;
    }
    setError('');
    try {
      await onSubmit({ buyer_name: cleanName, buyer_mobile: cleanMobile });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not submit. Please try again.');
    }
  };

  const isCall = action === 'call';
  const Icon = isCall ? Phone : MessageCircle;
  const accentBg = isCall ? 'bg-[#0A3622] hover:bg-[#072718]' : 'bg-[#25D366] hover:bg-[#22C55E]';
  const ctaLabel = isCall ? 'Show Number & Call' : 'Continue to WhatsApp';
  const eyebrow = isCall ? 'Direct Line' : 'Live Chat';

  return (
    <div
      data-dealer-skin
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-md p-0 sm:p-6"
      data-testid="buyer-lead-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <div className="w-full sm:max-w-md bg-white shadow-2xl border-t-4 border-[#0A3622] sm:border-t-0 sm:border-l-4 relative">
        {/* Close */}
        <button
          onClick={onClose}
          disabled={submitting}
          className="absolute top-4 right-4 text-zinc-400 hover:text-zinc-900 disabled:opacity-50 p-1 transition-colors"
          data-testid="buyer-lead-close"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="px-7 pt-9 pb-5">
          <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#0A3622] mb-3">
            {eyebrow} · Step 1 of 1
          </div>
          <h3 className="font-display text-3xl md:text-4xl font-black tracking-[-0.02em] leading-none text-zinc-900">
            {isCall ? 'Call the dealer.' : 'Chat on WhatsApp.'}
          </h3>
          <p className="mt-3 text-sm text-zinc-500 leading-relaxed">
            Share your details so {context.dealer || 'the dealer'} can connect you back if needed.
          </p>
          {context.car && (
            <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 bg-zinc-100 font-mono-tech text-[10px] uppercase tracking-[0.22em] text-zinc-700">
              <span className="w-1.5 h-1.5 rounded-full bg-[#0A3622]" />
              {context.car}
            </div>
          )}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-7 pb-7 space-y-7">
          {/* Name field — bottom-border editorial style */}
          <div>
            <label className="block font-mono-tech text-[10px] uppercase tracking-[0.28em] text-zinc-500 mb-2">
              Your Name
            </label>
            <input
              ref={nameRef}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Ramesh Kumar"
              maxLength={80}
              className="w-full px-0 py-2 bg-transparent border-0 border-b-2 border-zinc-200 focus:border-[#0A3622] focus:ring-0 focus:outline-none text-zinc-900 placeholder:text-zinc-300 text-base"
              data-testid="buyer-lead-name"
              required
            />
          </div>

          {/* Mobile field */}
          <div>
            <label className="block font-mono-tech text-[10px] uppercase tracking-[0.28em] text-zinc-500 mb-2">
              Mobile Number
            </label>
            <div className="flex items-center border-b-2 border-zinc-200 focus-within:border-[#0A3622] transition-colors">
              <span className="font-mono-tech text-zinc-500 text-base pr-3 border-r border-zinc-300 mr-3">+91</span>
              <input
                type="tel"
                value={mobile}
                onChange={(e) => setMobile(e.target.value.replace(/[^0-9]/g, '').slice(0, 10))}
                placeholder="10-digit mobile"
                inputMode="numeric"
                pattern="[0-9]{10}"
                maxLength={10}
                className="flex-1 px-0 py-2 bg-transparent border-0 focus:ring-0 focus:outline-none text-zinc-900 placeholder:text-zinc-300 text-base font-mono-tech"
                data-testid="buyer-lead-mobile"
                required
              />
            </div>
          </div>

          {error && (
            <div
              className="font-mono-tech text-[11px] uppercase tracking-[0.18em] text-red-700 border-l-2 border-red-500 pl-3 py-1"
              data-testid="buyer-lead-error"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="buyer-lead-submit"
            className={`group w-full inline-flex items-center justify-between px-6 py-4 text-white font-bold text-sm uppercase tracking-[0.2em] transition-colors disabled:opacity-60 ${accentBg}`}
          >
            <span className="inline-flex items-center gap-2.5">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
              {submitting ? 'Connecting…' : ctaLabel}
            </span>
            {!submitting && (
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            )}
          </button>

          <p className="font-mono-tech text-[9px] uppercase tracking-[0.22em] text-zinc-400 text-center leading-relaxed">
            By continuing, you agree your contact is shared with this dealer for follow-up.
          </p>
        </form>
      </div>
    </div>
  );
};

export default BuyerLeadModal;
